import { useState, useEffect } from "react";
import { X } from "lucide-react";

const fmtPar = (v) => (v === 0 || v === null || v === undefined ? (v === 0 ? "E" : "—") : v > 0 ? `+${v}` : `${v}`);
const cell = (h, key) => (h.pickup ? "X" : (h[key] ?? "—"));

// Renders a 9-hole segment of the golf scorecard (grid of metric rows).
function Segment({ label, holes, isNet, isStable }) {
  const sum = (key) => holes.reduce((a, h) => a + (typeof h[key] === "number" ? h[key] : 0), 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <tbody>
          <tr className="bg-[#1B4D3E] text-white">
            <td className="px-2 py-1.5 font-bold text-left sticky left-0 bg-[#1B4D3E]">{label}</td>
            {holes.map((h) => <td key={h.hole} className="px-2 py-1.5 text-center font-semibold w-8">{h.hole}</td>)}
            <td className="px-2 py-1.5 text-center font-bold">TOT</td>
          </tr>
          <tr className="bg-gray-50 text-[#64748B]">
            <td className="px-2 py-1 text-left sticky left-0 bg-gray-50">Par</td>
            {holes.map((h) => <td key={h.hole} className="px-2 py-1 text-center">{h.par}</td>)}
            <td className="px-2 py-1 text-center font-semibold">{sum("par")}</td>
          </tr>
          <tr className="text-[#0F172A] font-semibold border-b border-gray-100">
            <td className="px-2 py-1.5 text-left sticky left-0 bg-white">Golpes</td>
            {holes.map((h) => {
              const under = typeof h.gross === "number" && h.gross < h.par;
              const over = typeof h.gross === "number" && h.gross > h.par;
              return (
                <td key={h.hole} className="px-2 py-1.5 text-center">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${h.pickup ? "text-amber-600" : under ? "bg-red-50 text-red-600 font-bold" : over ? "text-[#64748B]" : "text-[#0F172A]"}`}>
                    {cell(h, "gross")}
                  </span>
                </td>
              );
            })}
            <td className="px-2 py-1.5 text-center font-bold">{sum("gross") || "—"}</td>
          </tr>
          {isNet && (
            <tr className="text-[#1B4D3E] border-b border-gray-100">
              <td className="px-2 py-1 text-left sticky left-0 bg-white">Neto</td>
              {holes.map((h) => <td key={h.hole} className="px-2 py-1 text-center">{cell(h, "net")}</td>)}
              <td className="px-2 py-1 text-center font-semibold">{sum("net") || "—"}</td>
            </tr>
          )}
          {isStable && (
            <tr className="text-[#1B4D3E] font-semibold">
              <td className="px-2 py-1 text-left sticky left-0 bg-white">Pts</td>
              {holes.map((h) => <td key={h.hole} className="px-2 py-1 text-center">{h.points ?? "—"}</td>)}
              <td className="px-2 py-1 text-center font-bold">{sum("points")}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function ScorecardModal({ open, onClose, title, url, headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !url) return;
    setLoading(true); setError(null); setData(null);
    fetch(url, { credentials: "include", headers: headers || {} })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("No se pudo cargar el score"))))
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, url, headers]);

  if (!open) return null;
  const isNet = data?.mode === "net";
  const isStable = data?.scoring_format === "stableford";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="scorecard-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#1B4D3E] text-white px-5 py-3.5 flex items-center justify-between rounded-t-2xl z-10">
          <div className="min-w-0">
            <h3 className="font-bold text-base truncate" data-testid="scorecard-name">{data?.name || title || "Tarjeta de score"}</h3>
            <p className="text-[11px] text-white/70">
              {data?.category_name ? `${data.category_name} · ` : ""}
              {isStable ? "Stableford" : "Medal Play"}{isNet ? ` · NET ${data?.hcp_percent ?? 100}%` : " · Gross"}
              {data?.tee_name ? ` · ${data.tee_name}` : ""}
              {data?.handicap_index != null ? ` · HCP ${data.handicap_index}` : ""}
              {isNet && data?.playing_handicap != null ? ` · Juego ${data.playing_handicap}` : ""}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="scorecard-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-5">
          {loading && <div className="flex justify-center py-10"><div className="w-6 h-6 border-3 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>}
          {error && <p className="text-center text-sm text-red-600 py-8">{error}</p>}
          {!loading && !error && data && (data.rounds || []).length === 0 && (
            <p className="text-center text-sm text-[#94A3B8] py-8">Este jugador aún no tiene score capturado.</p>
          )}
          {!loading && !error && (data?.rounds || []).map((r) => {
            const front = r.holes.slice(0, 9);
            const back = r.holes.slice(9, 18);
            return (
              <div key={r.round_num} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`scorecard-round-${r.round_num}`}>
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <span className="font-bold text-sm text-[#0F172A]">Ronda {r.round_num}</span>
                  <div className="flex items-center gap-2 text-xs">
                    {r.result_status ? (
                      <span className={`px-2 py-0.5 rounded font-bold text-white ${r.result_status === "DQ" ? "bg-red-600" : "bg-amber-500"}`}>{r.result_status}</span>
                    ) : (
                      <>
                        <span className="text-[#64748B]">Gross <b className="text-[#0F172A]">{r.gross_total ?? "—"}</b></span>
                        {isNet && <span className="text-[#64748B]">Neto <b className="text-[#1B4D3E]">{r.net_total ?? "—"}</b></span>}
                        {isStable && <span className="text-[#64748B]">Pts <b className="text-[#1B4D3E]">{r.stableford_points ?? "—"}</b></span>}
                        <span className="text-[#64748B]">vs Par <b className="text-[#0F172A]">{fmtPar(isNet ? r.score_to_par_net : r.score_to_par_gross)}</b></span>
                      </>
                    )}
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  <Segment label="OUT" holes={front} isNet={isNet} isStable={isStable} />
                  <Segment label="IN" holes={back} isNet={isNet} isStable={isStable} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
